import React from 'react';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useBusinessProfile } from '../contexts/BusinessProfileContext';
import { Login } from './Auth';
import spottoLogo from '../assets/spotto-logo.png';

type ProtectedRouteProps = {
  children: ReactNode;
  /** If true, skips the business profile check (for setup pages) */
  skipProfileCheck?: boolean;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  skipProfileCheck = false
}) => {
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  const { hasProfile, isLoading: profileLoading } = useBusinessProfile();
  const location = useLocation();

  const userRoles: string[] = Array.isArray((user as any)?.roles) ? (user as any).roles : [];
  const isAgent = userRoles.includes('Agent');

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen bg-surface-app flex items-center justify-center">
        <img
          src={spottoLogo}
          alt="Spotto"
          className="w-24 h-24 animate-pulse"
        />
      </div>
    );
  }

  // Not authenticated - show login
  if (!isAuthenticated) {
    return <Login />;
  }

  // Agents work under someone else's business — they never own a profile,
  // so the business-profile gate doesn't apply to them.
  if (!skipProfileCheck && !isAgent) {
    // Show loading while checking profile
    if (profileLoading) {
      return (
        <div className="min-h-screen bg-surface-app flex items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <img
              src={spottoLogo}
              alt="Spotto"
              className="w-24 h-24 animate-pulse"
            />
            <p className="text-ink-secondary text-sm">Loading your profile...</p>
          </div>
        </div>
      );
    }

    // No business profile - redirect to setup
    if (!hasProfile) {
      // Don't redirect if already on setup page
      if (location.pathname !== '/setup/business-profile') {
        return <Navigate to="/setup/business-profile" replace />;
      }
    }
  }

  return <>{children}</>;
};
